import html2pdf from 'html2pdf.js';

// ---- French (CFA) number-to-words helper ----
const UNITS = ['', 'un', 'deux', 'trois', 'quatre', 'cinq', 'six', 'sept', 'huit', 'neuf', 'dix',
  'onze', 'douze', 'treize', 'quatorze', 'quinze', 'seize', 'dix-sept', 'dix-huit', 'dix-neuf'];
const TENS = ['', '', 'vingt', 'trente', 'quarante', 'cinquante', 'soixante'];

const below100Fr = (n, suppressS = false) => {
  if (n < 20) return UNITS[n];
  const ten = Math.floor(n / 10);
  const unit = n % 10;
  if (ten === 7) {
    if (unit === 0) return 'soixante-dix';
    if (unit === 1) return 'soixante et onze';
    return 'soixante-' + UNITS[10 + unit];
  }
  if (ten === 9) return 'quatre-vingt-' + UNITS[10 + unit];
  if (ten === 8) return unit === 0 ? (suppressS ? 'quatre-vingt' : 'quatre-vingts') : 'quatre-vingt-' + UNITS[unit];
  const word = TENS[ten];
  if (unit === 0) return word;
  if (unit === 1) return word + ' et un';
  return word + '-' + UNITS[unit];
};

const below1000Fr = (n, suppressS = false) => {
  const hundred = Math.floor(n / 100);
  const rem = n % 100;
  let str = '';
  if (hundred > 0) {
    str = hundred === 1 ? 'cent' : UNITS[hundred] + ' cent';
    if (rem === 0 && hundred > 1 && !suppressS) str += 's';
  }
  if (rem > 0) str = (str ? str + ' ' : '') + below100Fr(rem, suppressS);
  return str;
};

const numberToFrenchWords = (value) => {
  let num = Math.round(Number(value) || 0);
  if (num === 0) return 'zéro';
  const scales = [
    { value: 1000000000, single: 'milliard', plural: 'milliards' },
    { value: 1000000, single: 'million', plural: 'millions' },
    { value: 1000, single: 'mille', plural: 'mille' },
  ];
  let words = '';
  for (const s of scales) {
    const count = Math.floor(num / s.value);
    if (count > 0) {
      if (s.value === 1000) {
        // "mille" is invariable and never preceded by "un"; cent/vingt before it stay singular
        words += (count === 1 ? '' : below1000Fr(count, true) + ' ') + 'mille ';
      } else {
        words += below1000Fr(count) + ' ' + (count > 1 ? s.plural : s.single) + ' ';
      }
      num %= s.value;
    }
  }
  if (num > 0) words += below1000Fr(num);
  return words.trim();
};

/**
 * Ocean Fishing Bissau - Trip Print Templates
 * Produces two branded, monochrome (white bg / black text) documents in
 * French/English (no Arabic), all amounts in XOF:
 *   - Invoice (Facture): fish lines with calibre, price/kg, weight, totals.
 *   - Receipt (Reçu): cash amount received for a paid trip.
 *
 * Usage:
 *   const { printInvoice, downloadInvoicePdf, printReceipt, downloadReceiptPdf } =
 *     FishingTripPrint({ trip, organization });
 */
const FishingTripPrint = ({ trip, organization }) => {
  const companyName = organization?.name || 'Ocean Fishing Bissau';
  const address = organization?.address || '';
  const phone = organization?.phone || '';
  const email = organization?.email || '';
  const regNumber = organization?.registration_number || '';

  // Resolve a logo URL from org settings, supporting absolute, data and relative URLs.
  // Falls back to the Ocean Fishing Bissau brand asset shipped with the app.
  const resolveLogo = () => {
    const raw =
      organization?.invoice_template?.logo_url ||
      organization?.document_templates?.sales_invoice?.logo_url ||
      organization?.logo_url ||
      '';
    if (raw) {
      if (raw.startsWith('http') || raw.startsWith('data:')) return raw;
      return `${process.env.REACT_APP_BACKEND_URL}${raw}`;
    }
    // Default brand logo (public asset). Absolute URL so print popups can load it.
    return `${window.location.origin}/ocean-fishing-logo.png`;
  };
  const logoUrl = resolveLogo();

  // --- Data helpers ---
  const lines = (trip?.invoice_lines || []).filter(
    (l) => (Number(l.weight) || 0) > 0 || (Number(l.price) || 0) > 0
  );
  const totalFacture =
    trip?.total_facture != null
      ? Number(trip.total_facture)
      : lines.reduce((s, l) => s + (Number(l.price) || 0) * (Number(l.weight) || 0), 0);

  const money = (n) =>
    (Number(n) || 0).toLocaleString('fr-FR', { maximumFractionDigits: 0 });
  const kg = (n) =>
    (Number(n) || 0).toLocaleString('fr-FR', { maximumFractionDigits: 2 });

  // Amount in words (French / CFA convention)
  const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
  const amountInWords = capitalize(numberToFrenchWords(totalFacture)) + ' francs CFA';

  const shipName = trip?.ship_account_name || trip?.ship_account_code || '-';
  const customerName = trip?.customer_account_name || trip?.customer_account_code || '-';
  const cashName = trip?.cash_account_name || trip?.cash_account_code || '';
  const tripNo = trip?.trip_number || '';
  const tripDate = trip?.date || '';
  // Optional overrides so this template also serves standalone Sales Invoices
  const numberLabel = trip?.number_label || 'Voyage / Trip No';
  const showShip = !trip?.hide_ship;

  // Amount in words is intentionally omitted (kept simple, bilingual FR/EN).

  // --- Shared styles ---
  const getStyles = () => `
    body, .doc-container {
      font-family: Arial, Helvetica, sans-serif;
      font-size: 13px;
      color: #000;
      background: #fff;
      margin: 0;
      padding: 12px;
    }
    .header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 8px;
      border-bottom: 2px solid #000;
      padding-bottom: 8px;
    }
    .header-left { text-align: left; flex: 1; }
    .header-center { flex: 0 0 180px; display: flex; align-items: center; justify-content: center; }
    .header-right { flex: 1; }
    .header h2 { font-size: 18px; margin: 0 0 4px 0; letter-spacing: .5px; }
    .header p { margin: 1px 0; font-size: 12px; }
    .logo-img { max-height: 96px; max-width: 170px; object-fit: contain; }
    .doc-title {
      text-align: center;
      font-size: 20px;
      font-weight: bold;
      letter-spacing: 2px;
      margin: 10px 0;
      border: 2px solid #000;
      padding: 5px;
    }
    .meta-section {
      display: flex;
      justify-content: space-between;
      flex-wrap: wrap;
      gap: 6px;
      margin: 8px 0;
      border: 1px solid #000;
      padding: 8px 10px;
    }
    .meta-field { margin: 2px 12px 2px 0; font-size: 13px; }
    .meta-label { font-weight: bold; }
    table.items {
      width: 100%;
      border-collapse: collapse;
      margin: 8px 0 4px 0;
    }
    table.items thead { display: table-header-group; }
    table.items th {
      background: #000;
      color: #fff;
      padding: 6px 4px;
      font-size: 12px;
      border: 1px solid #000;
      font-weight: bold;
    }
    table.items td {
      font-size: 12px;
      padding: 4px 5px;
      border: 1px solid #000;
    }
    table.items tbody tr { page-break-inside: avoid; }
    .totals-section { display: flex; justify-content: flex-end; margin-top: 6px; }
    .totals-table { width: 320px; border-collapse: collapse; }
    .totals-table td { padding: 5px 10px; font-size: 14px; border: 1px solid #000; }
    .totals-table .label { text-align: right; font-weight: bold; }
    .totals-table .value { text-align: right; font-family: monospace; }
    .totals-table .grand-total td { font-size: 16px; font-weight: bold; border: 2px solid #000; }
    .amount-words {
      margin: 10px 0 4px 0;
      padding: 8px 10px;
      border: 1px solid #000;
      font-size: 13px;
    }
    .amount-words .lbl { font-weight: bold; }
    .amount-words .val { font-style: italic; }
    .receipt-amount-box {
      border: 2px solid #000;
      padding: 16px;
      margin: 24px 0;
      text-align: center;
    }
    .receipt-amount-label { font-size: 14px; font-weight: bold; margin-bottom: 8px; }
    .receipt-amount-value { font-size: 30px; font-weight: bold; font-family: monospace; }
    .receipt-line { margin: 10px 0; font-size: 14px; }
    .receipt-line .lbl { font-weight: bold; }
    .signature-area { display: flex; justify-content: space-between; margin-top: 48px; }
    .signature-box { width: 220px; text-align: center; }
    .signature-line { border-top: 1px solid #000; margin-top: 40px; padding-top: 5px; font-size: 12px; }
    .doc-footer { margin-top: 16px; font-size: 11px; text-align: center; color: #333; }
  `;

  const headerHtml = () => `
  <div class="header">
    <div class="header-left">
      <h2>${companyName}</h2>
      ${address ? `<p>${address}</p>` : ''}
      ${phone ? `<p>Tél / Tel: ${phone}</p>` : ''}
      ${email ? `<p>Email: ${email}</p>` : ''}
      ${regNumber ? `<p>NIF / Reg: ${regNumber}</p>` : ''}
    </div>
    <div class="header-center">
      ${logoUrl ? `<img class="logo-img" src="${logoUrl}" alt="${companyName}" />` : ''}
    </div>
    <div class="header-right"></div>
  </div>`;

  // --- Invoice body ---
  const getInvoiceBodyHtml = () => {
    const itemRows = lines
      .map((l, i) => {
        const lineTotal = (Number(l.price) || 0) * (Number(l.weight) || 0);
        return `
        <tr>
          <td style="text-align:center;width:34px;">${i + 1}</td>
          <td>${l.fish_name || ''}</td>
          <td style="text-align:center;">${l.calibre_name || '-'}</td>
          <td style="text-align:right;">${money(l.price)}</td>
          <td style="text-align:right;">${kg(l.weight)}</td>
          <td style="text-align:right;font-weight:bold;">${money(lineTotal)}</td>
        </tr>`;
      })
      .join('');

    const emptyRows = Math.max(0, 12 - lines.length);
    const emptyRowsHtml = Array(emptyRows)
      .fill(
        `<tr>
          <td>&nbsp;</td><td></td><td></td><td></td><td></td><td></td>
        </tr>`
      )
      .join('');

    return `
  ${headerHtml()}
  <div class="doc-title">FACTURE / INVOICE</div>
  <div class="meta-section">
    <div>
      <div class="meta-field"><span class="meta-label">Date:</span> ${tripDate}</div>
      <div class="meta-field"><span class="meta-label">${numberLabel}:</span> ${tripNo}</div>
    </div>
    <div>
      ${showShip ? `<div class="meta-field"><span class="meta-label">Navire / Ship:</span> ${shipName}</div>` : ''}
      <div class="meta-field"><span class="meta-label">Client / Customer:</span> ${customerName}</div>
    </div>
  </div>
  <table class="items">
    <thead>
      <tr>
        <th style="width:34px;">#</th>
        <th>Poisson / Fish</th>
        <th style="width:120px;">Calibre</th>
        <th style="width:100px;">Prix/kg<br/>Price/kg</th>
        <th style="width:100px;">Poids (kg)<br/>Weight</th>
        <th style="width:120px;">Total (XOF)</th>
      </tr>
    </thead>
    <tbody>
      ${itemRows}
      ${emptyRowsHtml}
    </tbody>
  </table>
  <div class="totals-section">
    <table class="totals-table">
      <tr class="grand-total">
        <td class="label">TOTAL (XOF)</td>
        <td class="value">${money(totalFacture)}</td>
      </tr>
    </table>
  </div>
  <div class="amount-words">
    <span class="lbl">Arrêtée la présente facture à la somme de :</span>
    <span class="val">${amountInWords}.</span>
  </div>
  <div class="signature-area">
    <div class="signature-box"><div class="signature-line">Le Vendeur / Seller</div></div>
    <div class="signature-box"><div class="signature-line">Le Client / Customer</div></div>
  </div>
  <div class="doc-footer">${companyName} — Merci / Thank you</div>`;
  };

  // --- Receipt body ---
  const getReceiptBodyHtml = () => `
  ${headerHtml()}
  <div class="doc-title">REÇU DE CAISSE / CASH RECEIPT</div>
  <div class="meta-section">
    <div>
      <div class="meta-field"><span class="meta-label">Date:</span> ${tripDate}</div>
      <div class="meta-field"><span class="meta-label">Voyage / Trip No:</span> ${tripNo}</div>
    </div>
    <div>
      <div class="meta-field"><span class="meta-label">Navire / Ship:</span> ${shipName}</div>
      <div class="meta-field"><span class="meta-label">Reçu de / Received from:</span> ${customerName}</div>
    </div>
  </div>
  <div class="receipt-line">
    <span class="lbl">Reçu la somme de / Received the sum of:</span>
  </div>
  <div class="receipt-amount-box">
    <div class="receipt-amount-label">MONTANT / AMOUNT (XOF)</div>
    <div class="receipt-amount-value">${money(totalFacture)}</div>
  </div>
  <div class="receipt-line">
    <span class="lbl">Soit :</span> <span style="font-style:italic;">${amountInWords}.</span>
  </div>
  <div class="receipt-line">
    <span class="lbl">En règlement de / In payment of:</span>
    Facture voyage / Trip invoice ${tripNo}
  </div>
  ${cashName ? `<div class="receipt-line"><span class="lbl">Caisse / Cash account:</span> ${cashName}</div>` : ''}
  <div class="signature-area">
    <div class="signature-box"><div class="signature-line">Reçu par / Received by</div></div>
    <div class="signature-box"><div class="signature-line">Cachet &amp; Signature</div></div>
  </div>
  <div class="doc-footer">${companyName} — Merci / Thank you</div>`;

  // --- Print & PDF engines ---
  const openPrint = (title, bodyHtml) => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    const html = `
<!DOCTYPE html>
<html dir="ltr">
<head>
  <meta charset="UTF-8">
  <title>${title}</title>
  <style>
    @page { size: A4; margin: 10mm; }
    ${getStyles()}
    @media print {
      body { padding: 0; background: #fff; }
      table.items thead { display: table-header-group; }
    }
  </style>
</head>
<body>
  ${bodyHtml}
  <script>
    var img = document.querySelector('.logo-img');
    if (img && !img.complete) {
      img.onload = function() { window.print(); };
      img.onerror = function() { window.print(); };
    } else {
      window.onload = function() { window.print(); };
    }
  </script>
</body>
</html>`;
    printWindow.document.write(html);
    printWindow.document.close();
  };

  const downloadPdf = async (filename, bodyHtml) => {
    const container = document.createElement('div');
    container.style.position = 'fixed';
    container.style.left = '-9999px';
    container.style.top = '0';
    container.style.width = '210mm';
    container.style.background = '#fff';

    const styleEl = document.createElement('style');
    styleEl.textContent = getStyles();
    container.appendChild(styleEl);

    const content = document.createElement('div');
    content.className = 'doc-container';
    content.innerHTML = bodyHtml;
    container.appendChild(content);

    document.body.appendChild(container);

    const img = container.querySelector('.logo-img');
    if (img && !img.complete) {
      await new Promise((resolve) => {
        img.onload = resolve;
        img.onerror = resolve;
        setTimeout(resolve, 3000);
      });
    }

    try {
      await html2pdf()
        .set({
          margin: [10, 10, 10, 10],
          filename,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true, letterRendering: true },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
          pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
        })
        .from(content)
        .save();
    } finally {
      document.body.removeChild(container);
    }
  };

  return {
    printInvoice: () => openPrint(`Invoice ${tripNo}`, getInvoiceBodyHtml()),
    downloadInvoicePdf: () => downloadPdf(`Invoice-${tripNo || 'trip'}.pdf`, getInvoiceBodyHtml()),
    printReceipt: () => openPrint(`Receipt ${tripNo}`, getReceiptBodyHtml()),
    downloadReceiptPdf: () => downloadPdf(`Receipt-${tripNo || 'trip'}.pdf`, getReceiptBodyHtml()),
  };
};

export default FishingTripPrint;
